import { useEffect, useState } from 'react';
import { Shield, ArrowRight, Lock, Server, Sparkles, AlertCircle, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Logo } from '../components/Logo';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Alert } from '../components/Alert';
import { Loader } from '../components/Loader';

const SSO_PROBE_KEY = 'cyrn.sso.probed';

export function LoginPage() {
  const { login } = useAuth();
  const [urlError, setUrlError] = useState<string | null>(null);
  // Start in "probing" state so we never flash a sign-in button at someone who
  // already has a live CYRN SSO session and is about to be logged in silently.
  const [probing, setProbing] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    const ssoNone = params.get('sso') === 'none';

    if (err) {
      if (err === 'state_mismatch') setUrlError('Authentication session expired or state mismatch. Please retry.');
      else if (err === 'missing_parameters') setUrlError('OAuth authorization response was incomplete.');
      else if (err === 'invalid_token') setUrlError('Identity token validation failed.');
      else setUrlError(`Authentication error: ${err}`);
      sessionStorage.setItem(SSO_PROBE_KEY, '1');
      setProbing(false);
      return;
    }

    // The silent probe came back empty: there is no CYRN SSO session, so show
    // the sign-in button rather than looping back to the IdP forever.
    if (ssoNone) {
      sessionStorage.setItem(SSO_PROBE_KEY, '1');
      window.history.replaceState({}, '', window.location.pathname);
      setProbing(false);
      return;
    }

    // Only probe once per browser session, otherwise a user who deliberately
    // signed out of Nexus would be bounced straight back in.
    if (sessionStorage.getItem(SSO_PROBE_KEY)) {
      setProbing(false);
      return;
    }

    sessionStorage.setItem(SSO_PROBE_KEY, '1');
    window.location.href = api.auth.silentUrl;
  }, []);

  if (probing) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'radial-gradient(ellipse at top, #111927 0%, #070b11 100%)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <Logo variant="stacked" size={48} tagline="UNIFIED CUSTOMER HUB" light="beams" />
          <Loader size={28} label="Checking for an existing CYRN session…" />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-6)',
        background: 'radial-gradient(ellipse at top, #111927 0%, #070b11 100%)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 'var(--space-2)' }}>
          <Logo variant="stacked" size={56} tagline="UNIFIED CUSTOMER HUB" light="beams" />
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 8 }}>
            Central tenancy, entitlements &amp; billing authority for CYRN services
          </p>
        </div>

        {urlError && (
          <Alert tone="danger" title="Sign-in failed" icon={<AlertCircle size={18} />}>
            {urlError}
          </Alert>
        )}

        <Card padding="var(--space-6)" glow style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <div style={{ borderBottom: '1px solid var(--border-subtle)', paddingBottom: 'var(--space-4)' }}>
            <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>Sign in to CYRN Nexus</h2>
            <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', marginTop: 4 }}>
              Protected by CYRN Auth enterprise single sign-on
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--bg-inset)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
              <Shield size={18} color="var(--accent)" />
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                Single Sign-On across <strong style={{ color: 'var(--text-primary)' }}>Hosting</strong> &amp; <strong style={{ color: 'var(--text-primary)' }}>Threat Intel</strong>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--bg-inset)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
              <Lock size={18} color="var(--accent)" />
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                Strict multi-tenant isolation keyed by Organisation
              </div>
            </div>
          </div>

          <Button
            size="lg"
            variant="primary"
            onClick={login}
            icon={<ArrowRight size={18} />}
            style={{ width: '100%', marginTop: 4 }}
          >
            Continue with CYRN SSO
          </Button>

          <div style={{ textAlign: 'center' }}>
            <a
              href={api.auth.switchUrl}
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-tertiary)',
                textDecoration: 'none',
              }}
            >
              Use a different account
            </a>
          </div>

          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
              Don&apos;t have an account or new to CYRN?
            </span>
            <a
              href="https://cyrn.fr"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 'var(--text-xs)',
                color: 'var(--accent)',
                textDecoration: 'none',
                fontWeight: 'var(--fw-medium)',
              }}
            >
              <span>Discover CYRN services at cyrn.fr</span>
              <ExternalLink size={13} />
            </a>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '11px', color: 'var(--text-tertiary)' }}>
            <Server size={12} />
            <span>Secure OAuth 2.0 PKCE handshake &mdash; no password ever reaches Nexus</span>
          </div>
        </Card>

        <div style={{ textAlign: 'center', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <Sparkles size={14} color="var(--accent)" />
          <span>CYRN Infrastructure &amp; Security Services</span>
        </div>
      </div>
    </div>
  );
}
